import { useState, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'

import { EntityAccountTypes } from '../../../shared/_types'
import { getSignedS3Url, uploadToSignedS3 } from '../../../shared/_api'
import { createAccount } from '../api/create-account'

import Checkbox from '../../../../components/Checkbox'
import CountryInput from '../../../../components/CountryInput'
import FileUpload from '../../../../components/FileUpload'
import FormFooter from '../../../shared/FormFooter'
import InfoModal from '../../../../components/InfoModal'
import NumberInput from '../../../../components/NumberInput'
import TextInput from '../../../../components/TextInput'
import TINInput from '../../../../components/TINInput'
import StateDropdown from '../../../../components/StateDropdown'
import ErrorAlert from '../../../../components/ErrorAlert'

const NewEntityAccount = () => {
  const [use_primary_investor_contact, setUse_primary_investor_contact] =
    useState(false)
  const [documentsLater, setDocumentsLater] = useState(false)
  const nav = useNavigate()
  const queryClient = useQueryClient()

  const { t } = useTranslation()
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EntityAccountTypes>({
    defaultValues: {
      type: 'foe_entity',
      subtype: 'single_llc',
      is_disregarded_entity: false,
      jurisdiction_of_registration: 'AL',
      address: {
        country: 'US',
        state: 'AL',
      },
      use_primary_investor_contact: false,
    },
  })

  const watchSubEntityType = useWatch({ control, name: 'subtype' })
  const watchAddress = useWatch({ control, name: 'address' })

  useEffect(() => {
    if (
      watchSubEntityType !== 'single_llc' &&
      watchSubEntityType !== 's_corp'
    ) {
      setValue('is_disregarded_entity', null)
    }
    if (watchSubEntityType !== 'multi_llc') {
      setValue('number_of_members', '')
    }
  }, [watchSubEntityType, setValue])

  const createAccountMutation = useMutation({
    mutationFn: (data: EntityAccountTypes) => {
      const payload = { ...data }
      if (use_primary_investor_contact) {
        delete payload.address
        delete payload.phone_number
      }
      const newData = {
        ...Object.fromEntries(
          Object.entries(payload).filter(([_key, value]) => value !== '')
        ),
        type: 'foe_entity' as const,
        number_of_members: parseInt(payload.number_of_members) || null,
      }

      return createAccount(newData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ownership-types'],
      })
      nav('/dashboard/ownership-types')
    },
    onError: (error: any) => {
      console.error(error.message)
    },
  })

  const onSubmit = handleSubmit(async (data) => {
    await createAccountMutation.mutateAsync(data)
  })

  return (
    <div className="p-6 xl:p-0">
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="mb-2 text-2xl font-bold text-content-black">
          {t('common:ownershipType')}
        </h1>
        <p className="mb-6 italic text-content-black">{t('common:entity')}</p>
        <p className="mb-6 text-16 text-content-black">
          Provide the details for this entity account.
        </p>
        {Object.keys(errors).length > 0 && <ErrorAlert />}
        <form onSubmit={onSubmit}>
          <div className="mb-5">
            <div>
              <p className="mb-6 text-[19px] font-medium">
                {t('common:entity')}
              </p>
              <div className="mb-6 flex flex-col gap-3">
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t('account:nameEntity')}
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t('account:titleEntity')}
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="mb-6">
                <span
                  className={`mb-2 block text-base font-normal ${
                    errors?.type?.message ? 'text-alert' : 'text-content-black'
                  }`}
                >
                  {t('account:entityType')}
                </span>
                <select
                  className={`w-full appearance-none border ${
                    errors?.type?.message ? 'border-alert' : 'border-input'
                  } bg-white bg-down-chevron bg-[center_right_12px] bg-no-repeat px-3 py-[10px] text-[15px] text-content-black disabled:opacity-50`}
                  {...register('subtype')}
                >
                  <option value="single_llc">Single Member LLC</option>
                  <option value="multi_llc">Multi Member LLC</option>
                  <option value="s_corp">S Corp</option>
                  <option value="c_corp">C Corp</option>
                  <option value="partnership">Limited Partnership</option>
                </select>
              </div>
              {watchSubEntityType === 'multi_llc' && (
                <div className="mb-6">
                  <Controller
                    name="number_of_members"
                    control={control}
                    rules={{ required: 'is required' }}
                    render={({ field, fieldState }) => (
                      <NumberInput
                        label={t('account:numberOfMembers')}
                        errorMessage={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                </div>
              )}
              <div className="mb-6">
                <Controller
                  name="tin"
                  control={control}
                  rules={{
                    required: 'is required',
                    validate: {
                      isLongEnough: (value) => {
                        const tin = value.replace(/-/g, '')
                        return tin.length === 9 || 'is not a valid TIN'
                      },
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TINInput
                      label={
                        <span>
                          {t('account:tinLabel')}{' '}
                          <InfoModal
                            modalContent={
                              <div className="md:px-8 md:pt-4 px-6">
                                <h2 className="mb-6 text-xl font-medium text-content-black">
                                  {t('account:tinLabel')}
                                </h2>
                                <p className="mb-6 font-sans text-16 font-normal text-content-black">
                                  {t('account:tinModalEntity')}
                                </p>
                              </div>
                            }
                          />
                        </span>
                      }
                      placeholder="__-_______"
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
              </div>
              {(watchSubEntityType === 'single_llc' ||
                watchSubEntityType === 's_corp') && (
                <div className="mb-6">
                  <Controller
                    name="is_disregarded_entity"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Checkbox
                        label={
                          <span>
                            {t('account:isDisregardedEntity')}{' '}
                            <InfoModal
                              modalContent={
                                <div className="md:px-8 md:pt-4 px-6">
                                  <h2 className="mb-6 text-xl font-medium text-content-black">
                                    {t('account:disregardedEntity')}
                                  </h2>
                                  <p className="font-sans text-16 font-normal text-content-black">
                                    <Trans i18nKey="account:deModal" />
                                    <br />
                                    <br />
                                    <Trans i18nKey="account:realEstateAdviceDisclaimer" />
                                  </p>
                                </div>
                              }
                            />
                          </span>
                        }
                        checked={field.value === true}
                        {...field}
                        errorMessage={fieldState.error}
                      />
                    )}
                  />
                </div>
              )}
              <div className="mb-6">
                <Controller
                  name="jurisdiction_of_registration"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <StateDropdown
                      errorMessage={fieldState.error}
                      label={t('account:jurisdictionOfRegistration')}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="mb-6">
                <Controller
                  name="use_primary_investor_contact"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      {...field}
                      onChange={() => {
                        field.onChange(!field.value)
                        setUse_primary_investor_contact(
                          !use_primary_investor_contact
                        )
                      }}
                      checked={field.value === true}
                      errorMessage={fieldState.error}
                      label={t('account:sameAddressEntity')}
                    />
                  )}
                />
              </div>
              {!use_primary_investor_contact && (
                <div>
                  <div className="mb-6">
                    <span
                      className={`mb-2 block text-base font-normal ${
                        errors?.phone_number?.message
                          ? 'text-alert'
                          : 'text-content-black'
                      }`}
                    >
                      {t('common:phoneNumber')}
                    </span>
                    <PhoneInput
                      name="phone_number"
                      className={`phone-input-wrapper mb-2 h-[42px] w-full rounded border ${
                        errors?.phone_number ? 'border-alert' : 'border-input'
                      } px-3 py-1.5 text-16 focus-within:outline-none focus-within:ring-1 focus-within:border-primary`}
                      defaultCountry="US"
                      countryOptionsOrder={['US']}
                      control={control}
                      initialValueFormat="national"
                      limitMaxLength={true}
                      rules={{
                        required: 'is required',
                        validate: {
                          isValidPhoneNumber: (value: string) =>
                            isValidPhoneNumber(value) ||
                            'Please enter a valid phone number.',
                        },
                      }}
                    />
                    {errors?.phone_number?.message && (
                      <p className="mb-1 mt-2 text-xs text-alert">
                        {errors?.phone_number?.message}
                      </p>
                    )}
                  </div>
                  <div className="mb-6">
                    <CountryInput
                      register={register('address.country')}
                      errorMessage={errors?.address?.country?.message}
                    />
                  </div>
                  <div className="mb-2">
                    <Controller
                      name="address.street"
                      control={control}
                      rules={{ required: 'is required' }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label={t('common:address')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="address.street2"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextInput
                          placeholder={t('common:address2')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="address.city"
                      control={control}
                      rules={{ required: 'is required' }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label={t('common:city')}
                          errorMessage={fieldState.error}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-6 flex gap-3">
                    <div className="w-1/2">
                      <Controller
                        name="address.state"
                        control={control}
                        rules={{
                          required:
                            watchAddress?.country === 'US'
                              ? 'is required'
                              : false,
                        }}
                        render={({ field, fieldState }) =>
                          watchAddress?.country === 'US' ? (
                            <StateDropdown
                              errorMessage={fieldState?.error?.message}
                              label={t('common:state')}
                              {...field}
                            />
                          ) : (
                            <TextInput
                              label={
                                watchAddress?.country === 'US'
                                  ? t('common:state')
                                  : t('common:stateProvinceRegion')
                              }
                              errorMessage={fieldState?.error?.message}
                              {...field}
                            />
                          )
                        }
                      />
                    </div>
                    <div className="w-1/2">
                      <Controller
                        name="address.zip_code"
                        control={control}
                        rules={{ required: 'is required' }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label={
                              watchAddress?.country === 'US'
                                ? t('common:zipCode')
                                : t('common:postalCode')
                            }
                            errorMessage={fieldState.error}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="mb-6 text-[19px] font-medium">
                {t('common:documents')}
              </div>
              {!documentsLater && (
                <>
                  <div className="mb-6 text-16 font-medium">
                    {t('account:certificateOfFormation')}
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="certificate_of_formation_uuid"
                      control={control}
                      rules={{ required: 'File is required' }}
                      render={({ field }) => (
                        <FileUpload
                          {...field}
                          accept=".pdf"
                          label={t('common:uploadDocument')}
                          uploadFileFunction={async (file: File) => {
                            const signedS3Resp = await getSignedS3Url(
                              {
                                fileName: file.name,
                                type: 'certificate_of_formation',
                              },
                              true // isDashboard = true
                            )
                            if (signedS3Resp.status === 200) {
                              const { fields, url, s3_obj_id } =
                                signedS3Resp.data
                              const uploadResp = await uploadToSignedS3(
                                file,
                                url,
                                fields
                              )
                              console.log(uploadResp)
                              setValue(
                                'certificate_of_formation_uuid',
                                s3_obj_id
                              )
                            }
                          }}
                        />
                      )}
                    />
                    {errors?.certificate_of_formation_uuid?.message && (
                      <span className="mb-1 text-xs text-alert">
                        {errors.certificate_of_formation_uuid.message}
                      </span>
                    )}
                    <p className="mt-4 text-center text-[13px]">
                      {t('common:pdfOnly')}
                    </p>
                  </div>
                  <div className="mb-6 text-16 font-medium">
                    {t('account:operatingAgreement')}
                  </div>
                  <div className="mb-6">
                    <Controller
                      name="operating_agreement_uuid"
                      control={control}
                      rules={{ required: 'File is required' }}
                      render={({ field }) => (
                        <FileUpload
                          {...field}
                          accept=".pdf"
                          label={t('common:uploadDocument')}
                          uploadFileFunction={async (file: File) => {
                            const signedS3Resp = await getSignedS3Url(
                              {
                                fileName: file.name,
                                type: 'operating_agreement',
                              },
                              true // isDashboard = true
                            )
                            if (signedS3Resp.status === 200) {
                              const { fields, url, s3_obj_id } =
                                signedS3Resp.data
                              const uploadResp = await uploadToSignedS3(
                                file,
                                url,
                                fields
                              )
                              console.log(uploadResp)
                              setValue('operating_agreement_uuid', s3_obj_id)
                            }
                          }}
                        />
                      )}
                    />
                    {errors?.operating_agreement_uuid?.message && (
                      <span className="mb-1 text-xs text-alert">
                        {errors.operating_agreement_uuid.message}
                      </span>
                    )}
                    <p className="mt-4 text-center text-[13px]">
                      {t('common:pdfOnly')}
                    </p>
                  </div>
                </>
              )}
              <div className="mb-6">
                <Checkbox
                  name="documentsLater"
                  onChange={() => {
                    setDocumentsLater(!documentsLater)
                    setValue('operating_agreement_uuid', '')
                    setValue('certificate_of_formation_uuid', '')
                  }}
                  label={t('account:entityFilesLater')}
                />
              </div>
            </div>
          </div>
          <FormFooter
            backLocation="/dashboard/ownership-types/new"
            submitFunc={onSubmit}
            disabled={createAccountMutation.isLoading}
          />
        </form>
      </div>
    </div>
  )
}

export default NewEntityAccount
